<template>
  <div class="viq-input">
    <div class="viq-input__container">
      <div class="viq-input__field-wrap">
        <textarea
          ref="textarea"
          v-model="inputText"
          class="viq-input__textarea"
          :placeholder="placeholder"
          rows="1"
          :disabled="isStreaming"
          @input="autoResize"
          @keydown="handleKeydown"
        />
        <button
          class="viq-input__submit"
          :disabled="!canSubmit"
          :title="isStreaming ? 'Stop' : 'Ask'"
          @click="handleSubmit"
        >
          <template v-if="!isStreaming">Ask</template>
          <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="3" y="3" width="10" height="10" rx="1" fill="currentColor" />
          </svg>
        </button>
      </div>

      <div class="viq-input__footer">
        <span class="viq-input__disclaimer">
          VIQ uses AI across Vixio's proprietary data only. Always verify critical information from official sources before making decisions.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ViqInputArea",
  data() {
    return {
      inputText: "",
    };
  },
  computed: {
    ...mapState("Viq", ["mode", "isStreaming", "activeConversation", "activeLens"]),
    canSubmit() {
      return this.inputText.trim().length > 0 || this.isStreaming;
    },
    placeholder() {
      return "Ask about regulations, requirements, deadlines, compliance obligations...";
    },
  },
  watch: {
    mode() {
      this.$nextTick(() => this.autoResize());
    },
  },
  mounted() {
    this.autoResize();
  },
  methods: {
    autoResize() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
    },
    handleKeydown(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.handleSubmit();
      }
    },
    handleSubmit() {
      if (this.isStreaming) {
        this.$store.dispatch("Viq/cancelStream");
        return;
      }

      const text = this.inputText.trim();
      if (!text) return;

      if (this.mode === "ask") {
        this.$store.dispatch("Viq/askQuestion", {
          question: text,
          conversationId: this.activeConversation?.id || null,
          lensId: this.activeLens?.id || null,
        });
      } else if (this.mode === "report") {
        this.$store.dispatch("Viq/selectReportType", null);
        this.$store.commit("Viq/SET_REPORT_BRIEFING", { topic: text });
        this.$store.commit("Viq/SET_REPORT_STEP", 0);
      }

      this.inputText = "";
      this.$nextTick(() => this.autoResize());
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-input {
  padding: 0 $viq-space-lg $viq-space-lg;
  background: transparent;

  &__container {
    width: 90%;
    margin: 0 auto;
  }

  &__field-wrap {
    display: flex;
    align-items: flex-end;
    gap: $viq-space-sm;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-xl;
    padding: $viq-space-sm $viq-space-sm $viq-space-sm $viq-space-lg;
    transition: border-color $viq-transition-fast, box-shadow $viq-transition-fast;

    &:focus-within {
      border-color: $viq-accent;
      box-shadow: 0 0 0 2px $viq-accent-light;
    }
  }

  &__textarea {
    flex: 1;
    border: none;
    outline: none;
    resize: none;
    font-size: $viq-font-base;
    font-family: inherit;
    line-height: 1.5;
    color: $viq-text-primary;
    background: transparent;
    max-height: 200px;
    padding: $viq-space-sm 0;

    &::placeholder {
      color: $viq-text-muted;
    }

    &:disabled {
      opacity: 0.5;
    }
  }

  &__submit {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    padding: 0 $viq-space-md;
    border: none;
    border-radius: $viq-radius-md;
    background: $viq-accent;
    color: #fff;
    font-size: $viq-font-sm;
    font-weight: 600;
    cursor: pointer;
    transition: background $viq-transition-fast, opacity $viq-transition-fast;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: $viq-accent-hover;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__export {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    background: transparent;
    color: $viq-text-muted;
    cursor: pointer;
    transition: background $viq-transition-fast, color $viq-transition-fast, border-color $viq-transition-fast;

    &:hover {
      background: $viq-bg-secondary;
      color: $viq-text-primary;
      border-color: $viq-text-muted;
    }
  }

  &__footer {
    padding-top: $viq-space-sm;
    text-align: center;
  }

  &__disclaimer {
    font-size: $viq-font-xs;
    color: $viq-text-muted;
    line-height: 1.4;
  }
}
</style>
